import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

function mostSpecific<T, K extends keyof T>(key: K, sources: ReadonlyArray<T | undefined>): T[K] | undefined {
	for (const source of sources) {
		if (source?.[key] !== undefined) return source[key];
	}
	return undefined;
}

export const FCM_PROMOTE_PAGE_DYNAMIC_PRICING_EXPERIMENT = defineFcm('promote_page_dynamic_pricing_experiment')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				experiment_name: { type: 'string', optional: true },
				category_tiers: {
					type: 'object',
					optional: true,
					shape: {
						premium: {
							type: 'object',
							shape: {
								multiplier: { type: 'number' },
								tags: { type: 'array', shape: { type: 'string' } },
							},
						},
						discount: {
							type: 'object',
							shape: {
								multiplier: { type: 'number' },
								tags: { type: 'array', shape: { type: 'string' } },
							},
						},
					},
				},
				state_tiers: {
					type: 'object',
					optional: true,
					shape: {
						premium: {
							type: 'object',
							shape: {
								multiplier: { type: 'number' },
								states: { type: 'array', shape: { type: 'string' } },
							},
						},
						discount: {
							type: 'object',
							shape: {
								multiplier: { type: 'number' },
								states: { type: 'array', shape: { type: 'string' } },
							},
						},
					},
				},
				// Per-market, unlike category_tiers/state_tiers (CHANGE-98817): set as a country-level FCM
				// override per market being rolled out (see withMerger below), rather than embedding a
				// country code in the JSON itself.
				petition_reach_tiers: {
					type: 'object',
					optional: true,
					shape: {
						local: { type: 'number' },
						national: { type: 'number' },
						global: { type: 'number' },
					},
				},
				exclusion_group_experiment_id: { type: 'string', optional: true },
			},
		}),
	)
	.withDefault(undefined)
	.withMerger(({ globalValue, countryValue, localeValue }) => {
		const sources = [localeValue, countryValue, globalValue];
		return {
			experiment_name: mostSpecific('experiment_name', sources),
			category_tiers: mostSpecific('category_tiers', sources),
			state_tiers: mostSpecific('state_tiers', sources),
			exclusion_group_experiment_id: mostSpecific('exclusion_group_experiment_id', sources),
			petition_reach_tiers: mostSpecific('petition_reach_tiers', sources),
		};
	})
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'Promote Page',
		description:
			'Controls the promote page dynamic pricing experiment: the category, state, and petition-type tier tables (with their multipliers) used to scale the ask ladder, and the dynamic experiment name. Every field (CHANGE-98817) can be overridden per country or locale: set just the field(s) you need to change at the country/locale level for that market, leaving the rest unset so they fall back to the global value (see mergeLogic). exclusion_group_experiment_id is the member id to check this experiment against in FCM_PROMOTE_PAGE_EXCLUSION_GROUP; leave unset to run unconditionally, bypassing the exclusion group entirely.',
		mergeLogic:
			'Every field is resolved independently, preferring locale over country over global: each of experiment_name/category_tiers/state_tiers/exclusion_group_experiment_id/petition_reach_tiers uses the most specific level that has it set, falling back down to the global value (undefined for petition_reach_tiers if no market has been rolled out yet).',
		template: {
			experiment_name: 'promote_page_dynamic_pricing_experiment',
			exclusion_group_experiment_id: 'promote_page_dynamic_pricing_experiment',
			category_tiers: {
				premium: {
					multiplier: 1.25,
					tags: [
						'national government',
						'due process',
						'public health',
						'land use & zoning',
						'local government',
						'wildlife & biodiversity',
						'immigration policy',
						'drug & medicine regulation',
					],
				},
				discount: {
					multiplier: 0.8,
					tags: [
						'consumer rights',
						'video games & online gaming',
						'technology regulation',
						'animal welfare',
						"workers' rights",
						'student issues',
						'cultural preservation',
						'data privacy',
					],
				},
			},
			state_tiers: {
				premium: {
					multiplier: 1.15,
					states: ['VT', 'NH', 'ME', 'HI', 'CO', 'SC', 'NY', 'MS', 'ND', 'CA', 'MD', 'AK'],
				},
				discount: {
					multiplier: 0.9,
					states: ['SD', 'TN', 'KS', 'MO', 'LA', 'MN', 'WV', 'WI', 'WY', 'MI', 'KY', 'IN', 'OH'],
				},
			},
		},
		deprecated: 'FCM no longer in use (all tests off, control prices kept) — safe to delete after 2026-11-10',
	});
